import React from 'react';
import { Link } from 'react-router';

import Icon, { IconName } from '~/components/Icon';
import { useSound } from '~/hooks/useSound';
import { cn } from '~/utils/cn';

export type CardVariant =
  'green' | 'red' | 'orange' | 'purple' | 'yellow' | 'blue';

export interface CardProps {
  label: string;
  icon: IconName;
  variant?: CardVariant;
  /** Secondary line under the title. Accepts a node so it can host a badge (riddle type, timer…). */
  description?: React.ReactNode;
  to?: string;
  onClick?: (e: React.MouseEvent) => void;
  soon?: boolean;
  className?: string;
  as?: React.ElementType;
}

/** Icon plate colors, taken from the Figma homepage. */
const iconPlateClasses: Record<CardVariant, string> = {
  green: 'bg-green-500',
  red: 'bg-red-500',
  orange: 'bg-[#ff622e]',
  purple: 'bg-purple-500',
  yellow: 'bg-[#ffc82e]',
  blue: 'bg-[#2D88A9]'
};

const Card = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, CardProps>(
  (
    {
      label,
      icon,
      variant = 'green',
      description,
      to,
      onClick,
      soon,
      className,
      as,
      ...props
    },
    ref
  ) => {
    const playSound = useSound('/sounds/click_003.mp3');

    const handleClick = (e: React.MouseEvent) => {
      playSound();
      onClick?.(e);
    };

    const Component = as || (onClick && !to ? 'button' : Link);
    const linkProps = Component === Link ? { to } : {};
    const buttonProps =
      Component === 'button' ? { type: 'button' as const } : {};

    return (
      <Component
        ref={ref}
        {...linkProps}
        {...buttonProps}
        href={Component !== Link && to ? to : undefined}
        data-cy={`${label}-link`}
        onClick={handleClick}
        className={cn(
          'group/card relative flex cursor-pointer gap-4 overflow-hidden rounded-2xl bg-black/25 p-4 text-left backdrop-blur-[32px]',
          'transition-transform duration-150 hover:-translate-y-0.5 focus-visible:-translate-y-0.5 active:translate-y-0',
          'md:h-[158px] md:flex-col md:justify-between md:gap-8',
          soon && 'pointer-events-none opacity-50',
          className
        )}
        {...props}
      >
        {/* Icon plate — sits after the text on mobile, before it on desktop. */}
        <span
          className={cn(
            'order-2 flex size-10 shrink-0 -rotate-3 items-center justify-center rounded-xl md:order-1',
            'shadow-[0px_6px_0px_0px_#00000033,0px_0px_0px_2px_#000,inset_0px_2px_0px_0px_#ffffff40,inset_0px_-3px_0px_0px_#00000040]',
            'transition-transform duration-150 group-hover/card:rotate-0',
            iconPlateClasses[variant]
          )}
          aria-hidden
        >
          <Icon name={icon} size={6} className="drop-shadow-bottom-2" />
        </span>

        <span className="order-1 flex min-w-0 grow flex-col gap-1 md:order-2 md:grow-0">
          <span className="h-xl block">{label}</span>
          {description && (
            <span className="font-inter drop-shadow-bottom-1 block text-sm leading-[22px] font-normal">
              {description}
            </span>
          )}
        </span>
      </Component>
    );
  }
);

Card.displayName = 'Card';

export default Card;
